import { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Wallet, ArrowUpRight, IndianRupee, PlusCircle } from "lucide-react";

/**
 * HospitalWallet — Virtual balance + Razorpay top-up.
 *
 * Mock-safe: when Razorpay is not configured, the backend returns a
 * `mock_wallet_*` order and this component skips the Checkout modal
 * and calls `/verify-recharge` directly with a synthetic payment_id.
 */
export default function HospitalWallet() {
  const [wallet, setWallet] = useState({ balance: 0, ledger: [] });
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(1000);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/orgs/me/wallet");
      setWallet(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => { load(); }, []);

  const openRazorpay = (order, prefill) => new Promise((resolve, reject) => {
    if (!window.Razorpay) {
      reject(new Error("Razorpay SDK not loaded. Please refresh the page."));
      return;
    }
    const rzp = new window.Razorpay({
      key: order.key_id,
      order_id: order.order_id,
      amount: order.amount,
      currency: order.currency,
      name: order.name,
      description: order.description,
      prefill,
      handler: (resp) => resolve(resp),
      modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
      theme: { color: "#0284c7" },
    });
    rzp.open();
  });

  const submit = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 100 || amt > 50000) {
      toast.error("Amount must be between ₹100 and ₹50,000");
      return;
    }
    setBusy(true);
    try {
      const { data: intent } = await api.post("/orgs/me/wallet/recharge-intent", { amount: amt });
      let payment_id;
      let signature = "";
      if (intent.razorpay_enabled) {
        const resp = await openRazorpay(intent, {});
        payment_id = resp.razorpay_payment_id;
        signature = resp.razorpay_signature;
      } else {
        // Sandbox / not-configured: hit the mock verify path directly.
        payment_id = `mock_pay_${Date.now()}`;
        signature = "mock-sig";
      }
      const { data: v } = await api.post("/orgs/me/wallet/verify-recharge", {
        order_id: intent.order_id,
        razorpay_payment_id: payment_id,
        razorpay_signature: signature,
      });
      toast.success(`Wallet recharged. New balance: ₹${v.balance}`);
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Recharge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 space-y-4" data-testid="wallet-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-full p-3 bg-sky-100 text-sky-700"><Wallet size={22} /></div>
          <div>
            <h3 className="font-heading text-xl font-semibold text-slate-900">UpchaarQ Wallet</h3>
            <p className="text-xs text-slate-500">Funds RGHS/Ayushman commissions & platform fees</p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="wallet-recharge-btn">
              <PlusCircle size={16} className="mr-1" /> Recharge
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Recharge Wallet</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">Amount (₹)</label>
              <Input
                type="number"
                min={100}
                max={50000}
                step={100}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="wallet-recharge-amount"
              />
              <div className="flex gap-2">
                {[500, 1000, 2500, 5000, 10000].map((v) => (
                  <button key={v} type="button" onClick={() => setAmount(v)}
                          className="px-2 py-1 rounded text-xs border border-slate-200 hover:bg-slate-50"
                          data-testid={`wallet-preset-${v}`}>
                    ₹{v}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500">Min ₹100 · Max ₹50,000 per transaction</p>
              <Button
                onClick={submit}
                disabled={busy}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="wallet-recharge-submit">
                {busy ? "Processing…" : "Proceed to Pay"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-slate-200 p-4 flex items-center gap-3">
        <IndianRupee size={22} className="text-emerald-600" />
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Current balance</p>
          <p className="text-3xl font-heading font-bold text-slate-900" data-testid="wallet-balance">
            ₹{Number(wallet.balance || 0).toLocaleString("en-IN")}
          </p>
        </div>
      </div>

      {wallet.ledger?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-600 mb-2 uppercase tracking-wide">Recent activity</p>
          <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
            {wallet.ledger.slice(0, 10).map((e) => (
              <div key={e.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-2">
                  <ArrowUpRight size={14} className={e.amount >= 0 ? "text-emerald-500" : "text-rose-500"} />
                  <span className="text-slate-700">
                    {e.type === "recharge" ? "Recharge" :
                     e.type === "gov_scheme_commission" ? "Gov-scheme commission" :
                     e.type}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={e.amount >= 0 ? "text-emerald-700" : "text-rose-700"}>
                    {e.amount >= 0 ? "+" : ""}₹{Math.abs(e.amount).toLocaleString("en-IN")}
                  </span>
                  <span className="text-xs text-slate-400">
                    {new Date(e.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
